/**
 * The game's size on screen. Normally the window's. But an iPhone home-screen app with a see-through
 * status bar (index.html) is drawn from the very top of the screen while its window (the layout viewport)
 * still stops a status bar's height short of the bottom (a WebKit quirk), and everything pinned to the
 * window (position: fixed) is cut off there, leaving a bare strip along the foot. There the page itself is
 * made the screen's height and the 3D view and the layer over it are laid on the page instead of pinned
 * (html.app-full, CSS --app-h): the page's content does reach the bottom of the screen.
 */
export function gameSize(): { w: number; h: number } {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const root = document.documentElement;
  const off = (): { w: number; h: number } => {
    root.style.removeProperty('--app-h');
    root.classList.remove('app-full');
    return { w, h };
  };
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (!standalone) return off();
  // iPhone reports the screen upright whatever the orientation.
  const portrait = window.matchMedia('(orientation: portrait)').matches;
  const screenH = portrait ? Math.max(screen.width, screen.height) : Math.min(screen.width, screen.height);
  const gap = screenH - h;
  if (gap < 0 || gap > 100) {
    // Not a status bar's worth: an iPad window or split screen (left alone), or a moment of a size in
    // between (the app opening, coming back, turning): keep what's set until the size settles (the game
    // checks again a moment later), rather than dropping the full screen and never getting it back.
    const cur = parseFloat(root.style.getPropertyValue('--app-h'));
    return root.classList.contains('app-full') && Number.isFinite(cur) ? { w, h: cur } : off();
  }
  // No gap at all: the same layout costs nothing, and holds if the window is still cut short.
  root.style.setProperty('--app-h', `${screenH}px`);
  root.classList.add('app-full');
  // The page is now taller than the window, so it could be scrolled (by a status bar's height): pinned at
  // the top, or everything shifts up and the bare strip shows at the foot again. Only the keyboard may move
  // it while typing (a name, a note); it goes back when typing ends.
  if (!typing()) pinTop();
  if (!pinning) {
    pinning = true;
    // iPhone put the page back where it "was" on reopening the app: never.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    window.addEventListener('scroll', () => {
      if (root.classList.contains('app-full') && !typing()) pinTop();
    }, { passive: true });
    // Typing ended (focus left a box, and didn't just move into another one).
    window.addEventListener('focusout', () => {
      if (root.classList.contains('app-full')) setTimeout(() => !typing() && pinTop(), 50);
    });
  }
  return { w, h: screenH };
}

let pinning = false;

function typing(): boolean {
  const a = document.activeElement;
  return !!a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA');
}

function pinTop(): void {
  if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
}

/**
 * Run `check` whenever the size may have settled: back in front, turned, and once a second in a home-screen
 * app (iPhone doesn't always say when its window size settles after the app opens or comes back).
 */
export function watchGameSize(check: () => void): void {
  const later = (): void => {
    check();
    window.setTimeout(check, 350);
  };
  window.addEventListener('orientationchange', later);
  window.addEventListener('pageshow', later);
  window.visualViewport?.addEventListener('resize', check);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) later();
  });
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) window.setInterval(check, 1000);
}
